// src/hooks/useCart.js
import { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'tapio_cart_v1'

function readCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeCart(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    window.dispatchEvent(new CustomEvent('tapio:cart-updated', { detail: items }))
  } catch {}
}

export function useCart() {
  const [items, setItems] = useState(() => readCart())

  useEffect(() => {
    const onStorage = (e) => { if (e.key === STORAGE_KEY) setItems(readCart()) }
    const onCustom  = (e) => setItems(e.detail || readCart())

    window.addEventListener('storage', onStorage)
    window.addEventListener('tapio:cart-updated', onCustom)

    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('tapio:cart-updated', onCustom)
    }
  }, [])

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id)
      let next
      if (existing) {
        next = prev.map((i) =>
          i.product_id === product.id
            ? { ...i, quantity: Math.min(99, i.quantity + qty) }
            : i
        )
      } else {
        next = [...prev, {
          product_id: product.id,
          name: product.name,
          price: Number(product.price),
          currency: product.currency || 'MAD',
          image: product.image || null,
          quantity: Math.min(99, Math.max(1, qty)),
        }]
      }
      writeCart(next)
      return next
    })
  }, [])

  const updateQty = useCallback((productId, qty) => {
    setItems((prev) => {
      const next = prev
        .map((i) => i.product_id === productId
          ? { ...i, quantity: Math.max(0, Math.min(99, qty)) }
          : i)
        .filter((i) => i.quantity > 0)
      writeCart(next)
      return next
    })
  }, [])

  const removeItem = useCallback((productId) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.product_id !== productId)
      writeCart(next)
      return next
    })
  }, [])

  const clear = useCallback(() => {
    writeCart([])
    setItems([])
  }, [])

  const count = items.reduce((s, i) => s + i.quantity, 0)
  const total = items.reduce((s, i) => s + i.price * i.quantity, 0)

  return { items, addItem, updateQty, removeItem, clear, count, total }
}